import jwtDecode from 'jwt-decode';
import { AuthService } from '../services/AuthService';
// import { cleanSession } from '../../../utils/intervention'

const AUTH_VAR = `${process.env.REACT_APP_BASE_NAME}_auth`;

export const getUser = () =>
	// eslint-disable-next-line no-extra-boolean-cast
	!!window.localStorage[AUTH_VAR]
		? JSON.parse(window.localStorage[AUTH_VAR])
		: {};

/**
 * Sets user in localStorage
 *
 * @param {*} user
 * @returns
 */
export const setUser = (user) => {
	window.localStorage[AUTH_VAR] = JSON.stringify(user);

	return null;
};

/** Returns user access token if logged in
 *
 * @returns {string}
 */
export const getToken = () => getUser().token || null;

export const getRefreshToken = () => getUser().refreshToken || null;
export const handleAuth = ({
	token,
	refreshToken,
	loggedInAt,
	id,
	email,
	firstName,
	lastName,
	name,
	roles,
	username,
	exp,
	theme = 'light',
}) =>
	setUser({
		token,
		refreshToken,
		loggedInAt,
		id,
		email,
		firstName,
		lastName,
		name,
		roles,
		username,
		exp,
		theme,
	});
export const renewToken = async (token, refreshToken) => {
	try {
		const { data = null } =
			(await AuthService.refreshToken(token, refreshToken)) || null;
		if (data && data?.token && data?.refresh_token) {
			let user = {};
			try {
				user = jwtDecode(data.token);
			} catch (e) {
				user = null;
			}

			const loggedInAt = new Date();
			if (user) {
				return handleAuth({
					...user,
					token: data.token,
					refreshToken: data.refresh_token,
					loggedInAt,
				});
			}
		}
		return false;
	} catch (e) {
		return false;
	}
};

export const getValidToken = async () => {
	const token = getToken();
	if (token) {
		const decoded = jwtDecode(token);
		if (decoded.exp * 1000 >= Date.now()) {
			return token;
		}
	}
	// todo fetch a new token
	const refreshToken = getRefreshToken();
	const res = await renewToken(token, refreshToken);
	if (res) {
		const newUser = getUser();
		return newUser.token;
	}
	return false;
};

/**
 * Checks if user is logged in
 *
 * @returns
 */
export const isLoggedIn = () => {
	const user = getUser();
	return !!user.token;
};

export const cleanSession = (reset = false) => {
	if (reset) {
		localStorage.clear();
	}
	return true;
};

/**
 * Logs the user out
 *
 * @param {*} callback
 * @returns
 */
export const logout = async () => {
	const token = getToken();
	if (token) {
		await AuthService.logout(token);
	}
	cleanSession(true);
	setUser({});
};

export const sessionExpired = () => {
	const { loggedInAt = new Date() } = getUser();
	const savedDate = new Date(loggedInAt);
	const now = new Date();
	if (savedDate.getDate() !== now.getDate()) {
		return true;
	}
	return false;
};

export const authSSO = async (code) => {
	if (code) {
		try {
			const res = await AuthService.loginWithSSO(code);
			if (res?.data && res?.data?.token && res?.data?.refresh_token) {
				let user = {};
				try {
					user = jwtDecode(res?.data.token);
				} catch (e) {
					user = null;
				}

				const loggedInAt = new Date();
				if (user) {
					return handleAuth({
						...user,
						token: res?.data.token,
						refreshToken: res?.data.refresh_token,
						loggedInAt,
					});
				}
			}
			return false;
		} catch (e) {
			return false;
		}
	}
	return false;
};

export const getUserSettings = async (token) => {
	if (token) {
		const { status, data } = await AuthService.getUserSettings(token);
		if (status.toString() === '200') {
			return data;
		}
	}
	return false;
};

const createUserFromJWTToken = (token) => {
	let user = {};
	try {
		user = jwtDecode(token);
	} catch (e) {
		user = null;
	}

	if (user) {
		return user;
	}
	return null;
};

export const authenticateUser = async (payload) => {
	const { status, data } = await AuthService.login(payload);

	if (status === 200) {
		if (data && data?.token && data?.refresh_token) {
			const user = createUserFromJWTToken(data.token);

			if (user) {
				return {
					success: true,
					data: {
						user,
						token: data.token,
						refreshToken: data.refresh_token,
					},
				};
			}
		} else if (data && data?.mfa) {
			return {
				success: true,
				data: {
					mfa: data.mfa,
				},
			};
		}

		if (data && data?.Error) {
			return {
				success: false,
				message: 'Login et/ou mot de passe incorrect',
			};
		}
	}

	let errorMessage = '';
	switch (status) {
		case 401:
			errorMessage = 'Login et/ou mot de passe incorrect';
			break;
		case 429:
			errorMessage =
				'Trop de tentative de connexion, veuillez ressayer dans 15 minutes';
			break;
		case 403:
			errorMessage = "Accès à l'application non autorisé";
			break;
		case 501:
			errorMessage =
				'Un problème de serveur est survenu, veuillez réessayer plus tard';
			break;
		default:
			errorMessage = 'Une erreur est survenue, veuillez réessayer';
			break;
	}

	return {
		success: false,
		message: errorMessage,
	};
};

export const handleDoubleAuthLogin = async (payload) => {
	if (payload) {
		const { status, data } = await AuthService.checkDoubleAuthCode(
			payload.code,
			payload.mfaToken,
		);

		if (status === 200) {
			if (data?.token && data?.refresh_token) {
				const user = createUserFromJWTToken(data?.token);
				return {
					success: true,
					payload: {
						user,
						token: data?.token,
						refreshToken: data?.refresh_token,
					},
				};
			}
		}

		return {
			success: false,
			payload: null,
			error: "Le code d'authentification est incorrect",
		};
	}

	return {
		success: false,
		payload: null,
		error: 'Une erreur est survenue veuillez réessayer',
	};
};

export const modifyPassword = async (payload) => {
	const { status, data } = await AuthService.modifyPassword(payload);

	if (status === 200) {
		return {
			success: true,
			data: {
				userMail: data.mailAddress,
			},
		};
	}

	let errorMessage = '';
	switch (status) {
		case 400:
			errorMessage = [
				{
					message:
						'Cet utilisateur doit utiliser les outils de réinitialisation du groupe FnacDarty.',
				},
			];
			break;
		case 422:
			errorMessage = data.violations;
			break;
		case 501:
			errorMessage = [
				{
					message:
						'Un problème de serveur est survenu, veuillez réessayer plus tard',
				},
			];
			break;
		default:
			errorMessage = [
				{
					message: 'Une erreur est survenue, veuillez réessayer',
				},
			];
			break;
	}

	return {
		success: false,
		message: errorMessage,
	};
};

const TranslatedErrorsMessages = [];
TranslatedErrorsMessages['Invalid token'] =
	'Impossible de réinitialiser le mot de passe car le lien est invalide. Veuillez renouveler votre demande.';
TranslatedErrorsMessages['Token expired'] =
	'Impossible de réinitialiser le mot de passe car le lien a expiré. Veuillez renouveler votre demande.';
TranslatedErrorsMessages.default =
	'Une erreur est survenue lors de la réinitialisation du mot de passe, veuillez réessayer plus tard.';

export const resetPassword = async (payload, token) => {
	const { status, data } = await AuthService.resetPassword(payload, token);

	if (status === 201) {
		return {
			success: true,
			data: {
				message: data.message,
			},
		};
	}

	let errorMessage = data.message;
	switch (status) {
		case 400:
			errorMessage = [
				{
					message: TranslatedErrorsMessages[data.message]
						? TranslatedErrorsMessages[data.message]
						: TranslatedErrorsMessages.default,
				},
			];
			break;
		case 404:
			errorMessage = data.message;
			break;
		case 422:
			errorMessage = data.violations;
			break;
		case 501:
			errorMessage = [
				{
					message:
						'Un problème de serveur est survenu, veuillez réessayer plus tard',
				},
			];
			break;
		default:
			errorMessage = [
				{
					message: 'Une erreur est survenue, veuillez réessayer',
				},
			];
			break;
	}

	return {
		success: false,
		message: errorMessage,
	};
};

export const callResetLink = async (payload) => {
	const { status, data } = await AuthService.callResetLink(payload);

	if (status === 201) {
		return {
			success: true,
			data: {
				message: data.message,
			},
		};
	}

	let errorMessage = '';
	switch (status) {
		case 400:
			errorMessage = data.detail;
			break;
		case 501:
			errorMessage = [
				{
					message:
						'Un problème de serveur est survenu, veuillez réessayer plus tard',
				},
			];
			break;
		default:
			errorMessage = [
				{
					message: 'Une erreur est survenue, veuillez réessayer',
				},
			];
			break;
	}

	return {
		success: false,
		message: errorMessage,
	};
};

/**
 * Checks if user is Admin
 *
 * @returns
 */
export const isAdmin = () => {
	const user = getUser();
	return user?.roles?.indexOf('ROLE_ADMIN') !== -1;
};
